import React, { useEffect, useMemo, useState } from 'react';
import { connect, useDispatch } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import { useParams } from 'react-router-dom';

import AdjustPrompt from 'components/admin/AdjustPrompt';
import ApproveEarningRulePrompt from 'components/admin/ApproveEarningRulePrompt';
import ForceBurningPrompt from 'components/admin/ForceBurningPrompt';
import UseCashDollarPrompt from 'components/admin/UseCashDollarPrompt';
import AuthButton from 'components/base/AuthButton';
import AuthButtonWithDropdown from 'components/base/AuthButtonWithDropdown';
import ContentSections from 'components/base/ContentSections';
import CustomBreadcrumb from 'components/base/CustomBreadcrumb';
import CustomListComponent from 'components/base/CustomListComponent';
import ListButtonsGroup from 'components/base/ListButtonsGroup';
import Loading from 'components/base/Loading';
import MergeCustomerPrompt from 'components/customer/MergeCustomerPrompt';
import CustomerAdditionalData from 'components/customer/additionalData/CustomerAdditionalData';
import CouponSection from 'components/customerDetail/CouponSection';
import CustomerEarningRuleList from 'components/customerDetail/CustomerEarningRuleList';
import CustomerExclusiveCampaignList from 'components/customerDetail/CustomerExclusiveCampaignList';
import CustomerPointsAndLevelList from 'components/customerDetail/CustomerPointsAndLevelList';
import CustomerRewardList from 'components/customerDetail/CustomerRewardList';
import CustomerStampCampaignList from 'components/customerDetail/CustomerStampCampaignList';
import MembershipSection from 'components/customerDetail/MembershipSection';
import PersonalInformation from 'components/customerDetail/PersonalInformation';
import PurchaseItems from 'components/customerDetail/PurchaseItems';
import {
  ADJUST_TYPES,
  CUSTOMER_DETAIL_TAB_NAME,
  CustomerStatus,
} from 'config/CustomEnums';
import { PermissionCodes } from 'config/PermissionCodes';
import { tierTypeConfig } from 'config/TierTypeConfig';

import { getHashKeyString } from 'utils';

import './CustomerOverviewDetail.scss';

function CustomerDetail({ customer, customerProfileLoading }) {
  const history = useHistory();
  const location = useLocation();
  const dispatch = useDispatch();
  const params = useParams();
  const id = params.id;

  const [adjustPrompt, setAdjustPrompt] = useState({
    show: false,
    adjustType: null,
    prefillData: {},
  });
  const [useCashDollarPrompt, setUseCashDollarPrompt] = useState({
    show: false,
  });
  const [showCashDoubleConfirm, setShowCashDoubleConfirm] = useState(false);
  const [approveEarningRulePrompt, setApproveEarningRulePrompt] = useState({
    show: false,
  });
  const [showMergeCustomerPrompt, setShowMergeCustomerPrompt] = useState(false);

  const activeTab =
    getHashKeyString(location.hash) || CUSTOMER_DETAIL_TAB_NAME.profile;

  const setActiveTab = (key) => {
    history.push({ pathname: location.pathname, hash: key, search: '' });
  };

  const tabs = [
    {
      name: CUSTOMER_DETAIL_TAB_NAME.profile,
      content: (
        <div className="scroll-container-common">
          <ContentSections
            sections={
              customerProfileLoading || !customer?.pk
                ? [<Loading />]
                : [
                    <PersonalInformation setActiveTab={setActiveTab} />,
                    <PurchaseItems />,
                    <CouponSection
                      setActiveTab={setActiveTab}
                      setAdjustPrompt={setAdjustPrompt}
                    />,
                    <MembershipSection />,
                    <CustomerAdditionalData />,
                  ]
            }
            hidePreview={true}
          />
        </div>
      ),
    },
    {
      name: CUSTOMER_DETAIL_TAB_NAME.exclusiveCampaign,
      content: <CustomerExclusiveCampaignList />,
    },
    {
      name: CUSTOMER_DETAIL_TAB_NAME.stampCampaign,
      content: <CustomerStampCampaignList setAdjustPrompt={setAdjustPrompt} />,
    },
    {
      name: CUSTOMER_DETAIL_TAB_NAME.earningRules,
      content: <CustomerEarningRuleList />,
    },
    {
      name: CUSTOMER_DETAIL_TAB_NAME.coupons,
      content: <CustomerRewardList />,
    },
    {
      name: CUSTOMER_DETAIL_TAB_NAME.tickets,
      content: <CustomerRewardList isTicket />,
    },
    {
      name: tierTypeConfig.levels
        ? CUSTOMER_DETAIL_TAB_NAME.pointAndLevel
        : CUSTOMER_DETAIL_TAB_NAME.pointWithoutLevel,
      content: <CustomerPointsAndLevelList />,
    },
  ].filter((tab) => {
    if (
      !tierTypeConfig.segments &&
      !tierTypeConfig.levels &&
      tab.name === CUSTOMER_DETAIL_TAB_NAME.exclusiveCampaign
    ) {
      return false;
    }
    return true;
  });

  const buttons = useMemo(() => {
    if (customer?.isDeleted || !customer?.customerStatus) {
      return [];
    }

    const dimButton = [
      CustomerStatus.deactive,
      CustomerStatus.deleteRequested,
    ].includes(customer.customerStatus);
    const extraButtonClass = dimButton ? ' customer-status-btn-dimmed' : '';
    const extraButtons = [
      <AuthButton
        customClass="btn-back-button-common btn-download"
        title="Merge to primary account"
        action={() => {
          setShowMergeCustomerPrompt(true);
        }}
        requires={PermissionCodes.changeCustomer}
      />,
      <AuthButton
        customClass={'btn-back-button-common btn-download' + extraButtonClass}
        title="Approve earning rules"
        action={() => {
          if (dimButton) {
            return;
          }
          setApproveEarningRulePrompt({
            show: true,
          });
        }}
        requires={PermissionCodes.adjustAllRewards}
      />,
      <AuthButton
        customClass={'btn-back-button-common btn-download' + extraButtonClass}
        title="Use cash dollar"
        action={() => {
          if (dimButton) {
            return;
          }
          if (
            [
              CustomerStatus.temporaryEarning,
              CustomerStatus.temporary,
              CustomerStatus.prospect,
            ].includes(customer.customerStatus)
          ) {
            setShowCashDoubleConfirm(true);
            return;
          }
          setUseCashDollarPrompt({
            show: true,
          });
        }}
        requires={PermissionCodes.useCashDollar}
      />,
      <AuthButtonWithDropdown
        className={'btn-download' + extraButtonClass}
        permissionRequires={PermissionCodes.adjustAllRewards}
        title="Adjust"
        disableDropdown={dimButton}
        options={[
          {
            title: 'Adjust coupon',
            action: () => {
              setAdjustPrompt({
                show: true,
                adjustType: ADJUST_TYPES.coupon,
              });
            },
          },
          {
            title: 'Adjust ticket',
            action: () => {
              setAdjustPrompt({
                show: true,
                adjustType: ADJUST_TYPES.ticket,
              });
            },
          },
          {
            title: 'Adjust stamp',
            action: () => {
              setAdjustPrompt({
                show: true,
                adjustType: ADJUST_TYPES.stamp,
              });
            },
          },
        ]}
      />,
      // <AuthButton
      //   title="Edit"
      //   action={() => {
      //     dispatch({ type: 'customerList/clearData' });
      //     history.push({
      //       pathname: 'edit/',
      //     });
      //   }}
      //   requires={PermissionCodes.changeCustomer}
      // />,
    ];
    const extraPopContent = [
      {
        requires: PermissionCodes.changeCustomer,
        action: () => {
          setShowMergeCustomerPrompt(true);
        },
        content: 'Merge to primary account',
      },
      {
        requires: PermissionCodes.adjustAllRewards,
        action: () => {
          setApproveEarningRulePrompt({
            show: true,
          });
        },
        content: 'Approve earning rules',
      },
      {
        requires: PermissionCodes.useCashDollar,
        action: () => {
          setUseCashDollarPrompt({
            show: true,
          });
        },
        content: 'Use cash dollar',
      },
      {
        requires: PermissionCodes.adjustAllRewards,
        action: () => {
          setAdjustPrompt({
            show: true,
            adjustType: ADJUST_TYPES.coupon,
          });
        },
        content: 'Adjust coupon',
      },
      {
        requires: PermissionCodes.adjustAllRewards,
        action: () => {
          setAdjustPrompt({
            show: true,
            adjustType: ADJUST_TYPES.ticket,
          });
        },
        content: 'Adjust ticket',
      },
      {
        requires: PermissionCodes.adjustAllRewards,
        action: () => {
          setAdjustPrompt({
            show: true,
            adjustType: ADJUST_TYPES.stamp,
          });
        },
        content: 'Adjust stamp',
      },
      // {
      //   requires: PermissionCodes.adjustAllRewards,
      //   action: () => {
      //     dispatch({ type: 'customerList/clearData' });
      //     history.push({
      //       pathname: 'edit/',
      //     });
      //   },
      //   content: 'Edit',
      // },
    ];
    return [
      <ListButtonsGroup
        hideExtraButtonWidth={500}
        extraButtons={extraButtons}
        extraPopContent={extraPopContent}
      />,
    ];
  }, [customer?.isDeleted, customer?.customerStatus]);

  useEffect(() => {
    dispatch({ type: 'customerList/clearData' });
    dispatch({ type: 'couponTransactions/clearData' });
    dispatch({ type: 'transactions/clearData' });
    dispatch({ type: 'earningRuleList/clearData' });
    dispatch({ type: 'singleCoupon/clearData' });
    dispatch({ type: 'campaignList/clearData' });
    dispatch({ type: 'levels/getLevelList' });
    dispatch({ type: 'customerList/getCustomerSelectableSegments' });
  }, [dispatch]);

  useEffect(() => {
    dispatch({
      type: 'customerList/getOneCustomer',
      payload: { id },
    });
    dispatch({
      type: 'customerList/getOneCustomerBehavioralData',
      payload: { id },
    });
  }, [dispatch, id]);

  return (
    <div className="customer">
      <CustomListComponent
        caution={{
          detail: '',
          title: customer.name,
        }}
        defaultActiveKey={activeTab}
        breadcrumb={<CustomBreadcrumb name={customer.name} />}
        buttons={buttons}
        tabs={tabs}
        onTabChange={(key) => {
          setActiveTab(key);
        }}
      />
      <AdjustPrompt
        show={adjustPrompt.show}
        adjustType={adjustPrompt.adjustType}
        prefillData={{
          ...(adjustPrompt.prefillData || {}),
          targetCustomer: customer,
        }}
        customer={customer}
        setShow={(value) => setAdjustPrompt({ show: value })}
      />
      <ForceBurningPrompt
        customerStatus={customer.status}
        show={showCashDoubleConfirm}
        onCancel={() => setShowCashDoubleConfirm(false)}
        onConfirm={() => {
          setShowCashDoubleConfirm(false);
          setUseCashDollarPrompt({
            show: true,
          });
        }}
      />
      <UseCashDollarPrompt
        show={useCashDollarPrompt.show}
        customer={customer}
        setShow={(value) => setUseCashDollarPrompt({ show: value })}
      />
      <ApproveEarningRulePrompt
        show={approveEarningRulePrompt.show}
        customer={customer}
        setShow={(value) => setApproveEarningRulePrompt({ show: value })}
      />
      <MergeCustomerPrompt
        show={showMergeCustomerPrompt}
        setShow={setShowMergeCustomerPrompt}
      />
    </div>
  );
}

const mapPropsToState = (state) => ({
  customer: state.customerList.customer,
  customerProfileLoading: state.customerList.customerProfileLoading,
});

export default connect(mapPropsToState)(CustomerDetail);
